import _createClass from "@babel/runtime/helpers/createClass";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";
import _wrapNativeSuper from "@babel/runtime/helpers/wrapNativeSuper";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import { getBitValueFromBool, getBoolFromBitValue } from '../../common/utils/helpers';
import { ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY, DEFAULT_PREFERENCES, V001_SCHEMA_KEY, V002_SCHEMA_KEY, V003_SCHEMA_KEY } from '../../constants';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { getCookie } from '../../controllers/cookie-controls/get-cookie';
import { ContextualConsentProperty, PreferenceCategory } from '../../types';
import { ConsentHubKey } from './types';
var isLegacyGeoTargetingV2Token = function isLegacyGeoTargetingV2Token(consentToken) {
  var version = consentToken[0] + consentToken[1] + consentToken[2];
  var isV2Schema = version === V002_SCHEMA_KEY;
  return isV2Schema;
};
var isLegacyV1Token = function isLegacyV1Token(consentToken) {
  var version = consentToken[0] + consentToken[1] + consentToken[2];
  var isV1Schema = version === V001_SCHEMA_KEY;
  return isV1Schema;
};
export var hasLegacyV2TokenConsentedPreviously = function hasLegacyV2TokenConsentedPreviously(consentToken) {
  if (isLegacyGeoTargetingV2Token(consentToken)) {
    // If we're looking at a V2 token and they have ANY saved 'consentedUnderXX' bits,
    // we know it must be a set of valid consents instead of defaults
    var consentedUnderGDPR = consentToken[8] === '1';
    var consentedUnderCPRA = consentToken[9] === '1';
    var consentedUnderLGPD = consentToken[10] === '1';
    var consentedUnderFADP = consentToken[11] === '1';
    var hasConsentedPreviously = consentedUnderGDPR || consentedUnderCPRA || consentedUnderLGPD || consentedUnderFADP;
    return hasConsentedPreviously;
  }
  return false;
};
export var checkIfTokenIsDefault = function checkIfTokenIsDefault(consentToken) {
  var isLegacyV1ConsentToken = isLegacyV1Token(consentToken);
  var isLegacyV2ConsentToken = isLegacyGeoTargetingV2Token(consentToken);
  if (isLegacyV1ConsentToken) {
    // We only add a legacy `atl-gdpr-consents` cookie if a user has consented,
    // so if we see a V1 token, we know it must be a set of valid consents
    // instead of default consents
    return false;
  }
  if (isLegacyV2ConsentToken) {
    var legacyTokenHasConsents = hasLegacyV2TokenConsentedPreviously(consentToken);
    return !legacyTokenHasConsents;
  }

  // Check 'isDefaultConsentBit' bit for V003 Schema tokens
  var isDefaultTokenV3 = getBoolFromBitValue(consentToken[7]);
  return isDefaultTokenV3;
};
export var getDefaultBits = function getDefaultBits(consentsUnavailable) {
  var version = V003_SCHEMA_KEY;

  // DEFAULT_PREFERENCES now has the default boolean baked into it, so we don't need to set it here
  var serializedBits = Object.entries(DEFAULT_PREFERENCES).reduce(function (acc, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      val = _ref2[1];
    // Skip the STRICTLY_NECESSARY as it's always allowed
    if (key === PreferenceCategory.StrictlyNecessary) {
      return acc;
    }
    if (key === ContextualConsentProperty.UserIsAuthenticated) {
      // If consents are missing when setting these defaults, the user will be logged out,
      // so pack this info into the consent token bits
      var userIsAuthed = !consentsUnavailable;
      return acc + getBitValueFromBool(userIsAuthed);
    }
    if (key === ContextualConsentProperty.ConsentDataUnavailable) {
      // If consents are missing when setting these defaults, the user will be logged out,
      // so pack this info into the consent token bits
      return acc + getBitValueFromBool(!!consentsUnavailable);
    }
    return acc + getBitValueFromBool(val);
  }, '');
  return "".concat(version).concat(serializedBits);
};
export var getSavedConsentBits = function getSavedConsentBits(consents) {
  var _consents$find, _consents$find2, _consents$find3, _consents$find4;
  var version = V003_SCHEMA_KEY;
  // As 'strictlyNecessaryConsent' would always be allowed, we don't store it in the cookie or use ConsentHub
  var functionalConsent = getBitValueFromBool(!!((_consents$find = consents.find(function (key) {
    return key.key === ConsentHubKey.Functional;
  })) !== null && _consents$find !== void 0 && _consents$find.granted));
  var analyticsConsent = getBitValueFromBool(!!((_consents$find2 = consents.find(function (key) {
    return key.key === ConsentHubKey.Analytics;
  })) !== null && _consents$find2 !== void 0 && _consents$find2.granted));
  var marketingConsent = getBitValueFromBool(!!((_consents$find3 = consents.find(function (key) {
    return key.key === ConsentHubKey.Marketing;
  })) !== null && _consents$find3 !== void 0 && _consents$find3.granted));
  var unknownConsent = getBitValueFromBool(!!((_consents$find4 = consents.find(function (key) {
    return key.key === ConsentHubKey.Unknown;
  })) !== null && _consents$find4 !== void 0 && _consents$find4.granted));

  // As these consents were returned from the server, ConsentHub is obviously available, they're logged in, and aren't defaults
  var isDefaultToken = 0;
  var userIsAuthenticated = 1;
  var consentHubDataUnavailable = 0;
  return [version, functionalConsent, analyticsConsent, marketingConsent, unknownConsent, isDefaultToken, userIsAuthenticated, consentHubDataUnavailable].join('');
};
export var serializeConsents = function serializeConsents(_ref3) {
  var consents = _ref3.consents;
  if (!consents) {
    // When generating consents for an unauthenticated user, use the fallback token if it exists
    // This circumvents us setting defaults for them if their token has expired, but they have consented previously
    var existingFallbackToken = getCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY);
    if (existingFallbackToken) {
      return existingFallbackToken;
    }
    // User is unauthed, will only use local consenting, consent data will also be unavailable
    return getDefaultBits(true); // e.g. 0030000101
  } else if (consents.length === 0) {
    // User is authed but has no consents in the system, so use defaults in the interim, consentData must be available
    return getDefaultBits(); // e.g. 0030000110
  } else {
    // User is authed and has consents in the system
    return getSavedConsentBits(consents); // e.g. 0030110110
  }
};
export var deserializeConsents = function deserializeConsents(unpackedUserPreferences, displayedTextMap) {
  var validConsentHubCookieConsents = [PreferenceCategory.StrictlyNecessary, PreferenceCategory.Functional, PreferenceCategory.Analytics, PreferenceCategory.Marketing, PreferenceCategory.Unknown];
  var consentKeyMap = _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, PreferenceCategory.StrictlyNecessary, ConsentHubKey.StrictlyNecessary), PreferenceCategory.Functional, ConsentHubKey.Functional), PreferenceCategory.Analytics, ConsentHubKey.Analytics), PreferenceCategory.Marketing, ConsentHubKey.Marketing), PreferenceCategory.Unknown, ConsentHubKey.Unknown);
  var consentRecords = validConsentHubCookieConsents.map(function (consentKey) {
    var key = consentKeyMap[consentKey];
    return {
      key: key,
      granted: !!unpackedUserPreferences[consentKey],
      gatheredTs: new Date().toISOString(),
      displayedText: displayedTextMap[consentKey]
    };
  });
  return consentRecords;
};
export var getConsentTimestamp = function getConsentTimestamp(_ref4) {
  var consents = _ref4.consents;
  if (consents && consents.length) {
    // As consents can be updated individually, we need to find the most recent timestamp to use as "most recent consent"

    var timestamps = consents.map(function (consent) {
      return new Date(consent.updatedTs).getTime();
    });
    var maxTimestamp = Math.max.apply(Math, _toConsumableArray(timestamps));
    var mostRecentConsentTimestamp = new Date(maxTimestamp).toISOString();
    return mostRecentConsentTimestamp;
  }
};
export var checkIfConsentsAreStale = function checkIfConsentsAreStale(consentTimestamp) {
  // Privacy has determined that we should re-prompt users for consent if they haven't
  // consented in over a year regardless, because they're legally stale/expired
  var consentedOverAYearAgo = false;
  if (consentTimestamp) {
    var oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    consentedOverAYearAgo = new Date(consentTimestamp) < oneYearAgo;
  }
  return consentedOverAYearAgo;
};
export var StatusCodeError = /*#__PURE__*/function (_Error) {
  function StatusCodeError(message, status) {
    var _this;
    _classCallCheck(this, StatusCodeError);
    _this = _callSuper(this, StatusCodeError, [message]);
    _this.status = status;
    Object.setPrototypeOf(_this, StatusCodeError.prototype);

    // Captures the stack trace (for environments that support it)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(_this, StatusCodeError);
    }
    _this.name = 'StatusCodeError';
    return _this;
  }
  _inherits(StatusCodeError, _Error);
  return _createClass(StatusCodeError);
}( /*#__PURE__*/_wrapNativeSuper(Error));